<template>
  <div class="get-started-container">
    
    <div class="content-container">
      <div class="page-title-container">
        <h1 class="page-title">Get Started with <span class="highlight">CrowdHrs</span></h1>
        <p class="page-subtitle">Choose your account type to begin</p>
      </div>
      
      <div v-if="loading" class="loading-container">
        <Skeleton class="skeleton-card" width="100%" height="300px" />
        <Skeleton class="skeleton-card" width="100%" height="300px" />
        <Skeleton class="skeleton-card" width="100%" height="300px" />
      </div>
      
      <div v-else class="cards-container">
        <div 
          v-for="(item, index) in gettingStarted" 
          :key="index"
          class="card-wrapper"
        >
          <div class="custom-card">
            <div class="card-decoration">
              <div class="decoration-circle"></div>
              <div class="decoration-line"></div>
            </div>
            
            <div class="card-header">
              <div class="card-icon-wrapper">
                <i :class="getIconClass(index)"></i>
              </div>
            </div>
            
            <div class="card-content">
              <h2 class="card-title">{{ item.title }}</h2>
              <div class="card-tag">{{ getCardTag(index) }}</div>
              <p class="card-description">{{ item.desc }}</p>
            </div>
            
            <router-link
              :to="{ name: item.name }"
              class="card-button-link"
            >
              <div class="card-button">
                <span>{{ item.buttonText }}</span>
                <i class="pi pi-arrow-right button-icon"></i>
              </div>
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import { post } from '@/api/Services'

export default {
  name: 'GetStarted',
  data() {
    return {
      gettingStarted: [],
      loading: true,
    }
  },
  async created() {
    await this.getStarted()
  },
  methods: {
    async getStarted() {
      await post('/Commons/Executer', { sqlId: 51 })
        .then((data) => {
          const structuredData = {
            Candidate: {
              title: '',
              desc: '',
            },
            Company: {
              title: '',
              desc: '',
            },
            Recruiter: {
              title: '',
              desc: '',
            },
          }

          data.forEach((item) => {
            if (item.description === 'Sign Up - Candidate Title') {
              structuredData.Candidate.title = item.html_message
            } else if (item.description === 'Sign Up - Candidate Text') {
              structuredData.Candidate.desc = item.html_message
            } else if (item.description === 'Sign Up - Company Title') {
              structuredData.Company.title = item.html_message
            } else if (item.description === 'Sign Up - Company Text') {
              structuredData.Company.desc = item.html_message
            } else if (item.description === 'Sign Up - Recruiter Title') {
              structuredData.Recruiter.title = item.html_message
            } else if (item.description === 'Sign Up - Recruiter Text') {
              structuredData.Recruiter.desc = item.html_message
            }
          })

          this.gettingStarted.push(
            {
              title: structuredData.Candidate.title,
              desc: structuredData.Candidate.desc,
              buttonText: this.$t('clientButton'),
              name: 'signUp',
            },
            {
              title: structuredData.Company.title,
              desc: structuredData.Company.desc,
              buttonText: this.$t('companyButton'),
              name: 'companySignup',
            },
            {
              title: structuredData.Recruiter.title,
              desc: structuredData.Recruiter.desc,
              buttonText: this.$t('recruiterButton'),
              name: 'signUp',
            }
          )
        })
        .finally(() => {
          this.loading = false
        })
    },
    getIconClass(index) {
      const icons = [
        'pi pi-user',        // Candidate
        'pi pi-building',    // Company
        'pi pi-briefcase'    // Recruiter
      ];
      return icons[index] || 'pi pi-user';
    },
    getCardTag(index) {
      const tags = [
        'For Job Seekers',
        'For Organizations',
        'For Hiring Teams'
      ];
      return tags[index] || '';
    }
  },
}
</script>

<style lang="scss" scoped>
.get-started-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background:var(--bg-light);
}

.content-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3rem 1.5rem;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  background:var(--bg-light);
}

.page-title-container {
  text-align: center;
  margin-bottom: 3rem;
}

.page-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 0.5rem;
  
  .highlight {
    color: #015cac;
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
}

.page-subtitle {
  font-size: 1.25rem;
  color: #6c757d;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
}

.loading-container {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  width: 100%;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.skeleton-card {
  border-radius: 12px;
  overflow: hidden;
}

.cards-container {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2.5rem;
  width: 100%;
  padding: 1rem;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    max-width: 450px;
    margin: 0 auto;
  }
}

.card-wrapper {
  height: 100%;
  perspective: 1000px;
  
  &:hover {
    .custom-card {
      transform: translateY(-10px) rotateX(2deg);
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
      
      .decoration-circle {
        transform: translateX(10px) scale(1.2);
      }
      
      .decoration-line {
        width: 100px;
        opacity: 0.5;
      }
      
      .card-icon-wrapper {
        box-shadow: 0 15px 35px rgba(252, 108, 42, 0.3);
      }
      
      .card-button {
        background-color: darken(#fc6c2a, 5%);
        
        .button-icon {
          transform: translateX(6px);
        }
      }
    }
  }
}

.custom-card {
  height: 100%;
  background-color: #fff;
  border-radius: 20px;
  overflow: hidden;
  position: relative;
  transition: all 0.5s cubic-bezier(0.165, 0.84, 0.44, 1);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  min-height: 450px;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 5px;
    background: linear-gradient(90deg, #015cac, #fc6c2a);
    z-index: 3;
  }
}

.card-decoration {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 1;
  pointer-events: none;
  
  .decoration-circle {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(1, 92, 172, 0.15), rgba(1, 92, 172, 0.05));
    transition: transform 0.6s cubic-bezier(0.165, 0.84, 0.44, 1);
  }
  
  .decoration-line {
    position: absolute;
    top: 30px;
    right: 50px;
    width: 50px;
    height: 3px;
    background: linear-gradient(90deg, rgba(252, 108, 42, 0), rgba(252, 108, 42, 0.5));
    border-radius: 2px;
    transition: all 0.6s cubic-bezier(0.165, 0.84, 0.44, 1);
  }
}

.card-header {
  padding: 2rem 1.5rem 4rem;
  background: linear-gradient(135deg, rgba(1, 92, 172, 0.05) 0%, rgba(1, 92, 172, 0.02) 100%);
  position: relative;
  display: flex;
  justify-content: center;
  min-height: 150px;
}

.card-icon-wrapper {
  width: 90px;
  height: 90px;
  border-radius: 50%;
  position: absolute;
  top: calc(100% - 45px);
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #fff, #f8f9fa);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2;
  transition: all 0.5s cubic-bezier(0.165, 0.84, 0.44, 1);
  box-shadow: 0 8px 25px rgba(1, 92, 172, 0.2);
  
  i {
    font-size: 2.6rem;
    background: linear-gradient(135deg, #015cac, #fc6c2a);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
}

.card-content {
  padding: 3.5rem 1.75rem 2rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 2;
  position: relative;
}

.card-title {
  font-size: 1.6rem;
  font-weight: 700;
  color: #333;
  text-align: center;
  margin: 0 0 0.75rem;
  letter-spacing: -0.5px;
}

.card-tag {
  font-size: 0.85rem;
  font-weight: 600;
  color: #015cac;
  background-color: rgba(1, 92, 172, 0.08);
  padding: 0.4rem 1.2rem;
  border-radius: 20px;
  margin-bottom: 1.25rem;
  display: inline-block;
}

.card-description {
  font-size: 1.05rem;
  text-align: center;
  color: #555;
  line-height: 1.6;
  margin: 0;
  flex: 1;
}

.card-button-link {
  text-decoration: none;
  margin-top: auto;
  display: block;
}

.card-button {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background-color: #fc6c2a;
  color: white;
  padding: 1.25rem;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  .button-icon {
    transition: transform 0.3s ease;
    margin-left: 6px;
  }
}
</style>
